import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/States";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, UserPlus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export const MODULES = [
  { key: "order_management", label: "Order Management", desc: "Orders & delivery" },
  { key: "product_management", label: "Product Management", desc: "Products, master, categories, brands, approvals" },
  { key: "retailer_management", label: "Retailer Management", desc: "Retailers, stores, subscriptions, verification" },
  { key: "customer_management", label: "Customer Management", desc: "Customer accounts" },
  { key: "affiliate_management", label: "Affiliate Management", desc: "Influencers, commissions, withdrawals" },
  { key: "marketing", label: "Marketing", desc: "Banners, testimonials, FAQs, coupons" },
  { key: "payments", label: "Payments", desc: "Order & subscription payments" },
  { key: "support", label: "Support", desc: "Tickets & support agents" },
  { key: "reports", label: "Reports", desc: "Analytics & reports" },
  { key: "survey_crm", label: "Survey CRM", desc: "Retailer surveys, survey agents, follow-ups & exports" },
  { key: "shyama", label: "Shyama Foundation", desc: "Dairy finance applications, bank partners, farms, donations" },
];

const Table = ({ children }) => <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white"><table className="w-full text-sm">{children}</table></div>;
const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

function DangerZone() {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    const t = window.prompt("This permanently deletes ALL stores, products, orders, banners, reviews, coupons and non-admin users on THIS environment (production if you're on the live site). Categories, settings and admin accounts are kept.\n\nType RESET to confirm:");
    if (t !== "RESET") { if (t !== null) toast.error("Confirmation text did not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: "RESET" });
      const n = Object.values(data.cleared || {}).reduce((a, b) => a + b, 0);
      toast.success(`Platform reset — ${n} records cleared. Refreshing…`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/50 p-5" data-testid="admin-danger-zone">
      <h3 className="flex items-center gap-2 font-head font-bold text-red-600"><Trash2 className="h-5 w-5" /> Danger zone — reset platform data</h3>
      <p className="mt-1 text-sm text-slate-600">Clears all demo/showcase content (stores, products, orders, banners, reviews, coupons) and non-admin users on the current environment. Categories, settings, subscription plans and admin/sub-admin accounts are preserved. Use this to remove old demo data in production before uploading real data.</p>
      <button onClick={reset} disabled={busy} data-testid="admin-reset-data-btn" className="mt-3 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
        {busy ? "Resetting…" : "Reset all platform data"}
      </button>
    </div>
  );
}

export default function AdminSubAdmins() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sub-admins"], queryFn: () => api.get("/admin/sub-admins").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: [] });
  const [busy, setBusy] = useState(false);

  const toggle = (k) => setForm((f) => ({ ...f, permissions: f.permissions.includes(k) ? f.permissions.filter((p) => p !== k) : [...f.permissions, k] }));

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password are required");
    if (form.permissions.length === 0) return toast.error("Grant at least one module");
    setBusy(true);
    try {
      await api.post("/admin/sub-admins", form);
      toast.success("Sub-admin created");
      setForm({ name: "", email: "", password: "", permissions: [] });
      qc.invalidateQueries({ queryKey: ["sub-admins"] });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const update = async (id, payload) => {
    try { await api.patch(`/admin/sub-admins/${id}`, payload); qc.invalidateQueries({ queryKey: ["sub-admins"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this sub-admin? They will lose all access.")) return;
    try { await api.delete(`/admin/sub-admins/${id}`); toast.success("Removed"); qc.invalidateQueries({ queryKey: ["sub-admins"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetPassword = async (id) => {
    const pw = window.prompt("Enter a new password for this sub-admin:");
    if (!pw) return;
    await update(id, { password: pw });
    toast.success("Password updated (their sessions were signed out)");
  };

  return (
    <div className="space-y-5" data-testid="admin-subadmins-panel">
      <DangerZone />
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 flex items-center gap-2 font-head font-bold text-slate-900"><UserPlus className="h-5 w-5 text-brand" /> Create sub-admin</h3>
        <p className="mb-3 text-xs text-slate-500">Give a staff member access to only the areas they need. You (Super Admin) keep full control.</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="subadmin-name" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="subadmin-email" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="text" placeholder="Temporary password" data-testid="subadmin-password" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
        </div>
        <p className="mb-2 mt-4 text-xs font-bold uppercase tracking-wide text-slate-400">Permissions</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => {
            const on = form.permissions.includes(m.key);
            return (
              <button key={m.key} type="button" onClick={() => toggle(m.key)} data-testid={`subadmin-perm-${m.key}`}
                className={`rounded-xl border p-3 text-left transition-colors ${on ? "border-brand bg-brand-soft" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold ${on ? "text-brand" : "text-slate-700"}`}>{m.label}</span>
                  {on && <ShieldCheck className="h-4 w-4 text-brand" />}
                </div>
                <p className="mt-0.5 text-[11px] text-slate-500">{m.desc}</p>
              </button>
            );
          })}
        </div>
        <button onClick={create} disabled={busy} data-testid="subadmin-create-btn" className="mt-4 rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">Create sub-admin</button>
      </div>

      {!data ? <Spinner /> : data.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No sub-admins yet" subtitle="Create staff accounts above and grant them specific modules." />
      ) : (
        <Table>
          <thead><tr className="border-b border-slate-100"><Th>Name</Th><Th>Permissions</Th><Th>Active</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {data.map((s) => (
              <tr key={s.id} className="border-b border-slate-50 align-top">
                <Td><p className="font-semibold text-slate-800">{s.name}</p><p className="text-xs text-slate-400">{s.email}</p></Td>
                <Td>
                  <div className="flex max-w-md flex-wrap gap-1">
                    {(s.permissions || []).map((p) => (
                      <span key={p} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{MODULES.find((m) => m.key === p)?.label || p}</span>
                    ))}
                  </div>
                </Td>
                <Td><Switch checked={s.is_active} onCheckedChange={(v) => update(s.id, { is_active: v })} data-testid={`subadmin-active-${s.id}`} /></Td>
                <Td>
                  <div className="flex gap-1.5">
                    <button onClick={() => resetPassword(s.id)} data-testid={`subadmin-reset-${s.id}`} title="Reset password" className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"><KeyRound className="h-4 w-4" /></button>
                    <button onClick={() => remove(s.id)} data-testid={`subadmin-del-${s.id}`} title="Remove" className="rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
